import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';

import { listApprovals } from '../../utils/approvalsApi';
import ApprovalFilterTabs, { STATUS_TABS } from './components/ApprovalFilterTabs';
import ApprovalListTable from './components/ApprovalListTable';
import ApprovalEmptyState from './components/ApprovalEmptyState';

const VALID_STATUSES = new Set(STATUS_TABS.map((t) => t.value));
const POLL_MS = 30000;

const extractResults = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
};

const extractServerError = (err) => {
  if (!err) return null;
  if (err.response?.data?.error) return err.response.data.error;
  if (err.response?.data?.detail) return err.response.data.detail;
  return 'Something went wrong. Try again or contact support.';
};

const ApprovalsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStatus = searchParams.get('status') || 'all';
  const statusFilter = VALID_STATUSES.has(rawStatus) ? rawStatus : 'all';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState('');

  // Stable, primitive effect deps only. Polling is keyed by statusFilter
  // and an explicit refreshKey — no callback props or inline objects.
  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      try {
        setError(null);
        const res = await listApprovals({ status: statusFilter });
        if (cancelled) return;
        setRows(extractResults(res?.data));
      } catch (err) {
        if (cancelled) return;
        setError(extractServerError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, refreshKey]);

  // Separate polling effect — also keyed only by primitives.
  // 30s cadence per spec (NOT the 3s notes pattern).
  const refreshRef = useRef(() => {});
  refreshRef.current = () => setRefreshKey((k) => k + 1);
  useEffect(() => {
    const id = window.setInterval(() => {
      refreshRef.current();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [statusFilter]);

  const handleStatusChange = useCallback(
    (next) => {
      const nextParams = new URLSearchParams(searchParams);
      if (next && next !== 'all') {
        nextParams.set('status', next);
      } else {
        nextParams.delete('status');
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      const patient = (r.patient_name || '').toLowerCase();
      const provider = (r.provider_name || '').toLowerCase();
      return patient.includes(needle) || provider.includes(needle);
    });
  }, [rows, search]);

  const onActivate = useCallback(
    (id) => navigate(`/approvals/${id}`),
    [navigate],
  );

  const showEmpty = !loading && filteredRows.length === 0 && !error;

  return (
    <Box sx={{ bgcolor: '#F6F7FB', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
              Approvals
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Secure provider approval requests. Track each from draft to signed.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => setRefreshKey((k) => k + 1)}
              aria-label="Refresh approvals list"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => navigate('/approvals/new')}
            >
              New approval
            </Button>
          </Stack>
        </Stack>

        <ApprovalFilterTabs value={statusFilter} onChange={handleStatusChange} />

        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by patient or provider name"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Find
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 360, bgcolor: '#FFFFFF' }}
            inputProps={{ 'aria-label': 'Search approvals' }}
          />
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setRefreshKey((k) => k + 1)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {showEmpty ? (
          <ApprovalEmptyState statusFilter={statusFilter} />
        ) : (
          <ApprovalListTable rows={filteredRows} loading={loading} onActivate={onActivate} />
        )}
      </Container>
    </Box>
  );
};

export default ApprovalsPage;
